/**
 * Partners — the embedded lane from the seed deck, without naming partners.
 *
 * Who this is for: a product that already owns a population (a family app, a
 * disability benefits service, a care network) and needs the money that
 * reaches that population to move through government and philanthropic
 * funders. Partner names are deliberately absent until each partner agrees
 * to be named.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Banknote, Bot, FileCheck2, Layers, UserCheck } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import OneEncounter from '@/components/marketing/OneEncounter';
import { contactHref } from '@/components/marketing/siteFacts';

const WHAT_YOU_EMBED = [
  {
    icon: UserCheck,
    title: 'Eligibility and enrollment',
    body: 'Check Medicaid and other coverage, enroll members in the programs they qualify for, and keep the consent record that lets you do it.',
  },
  {
    icon: Banknote,
    title: 'Disbursement',
    body: 'Incentives, guaranteed income and provider payments on Trust cards, tied to the milestone or service that earned them.',
  },
  {
    icon: FileCheck2,
    title: 'Funder reporting',
    body: 'Each program reported in its funder’s own format, from the same record, with every dollar traced to the service behind it.',
  },
  {
    icon: Layers,
    title: 'Braided billing',
    body: 'When your members also qualify for Medicaid benefits such as CHW or doula services, those are billed alongside the cash program, on the same member.',
  },
];

const LANE_STEPS = [
  ['Land', 'Start with one program on one population, embedded in your product.'],
  ['Replicate', 'Every next program in the lane reuses the rules, disbursement and reporting already built.'],
  ['Braid', 'Add the Medicaid and plan funding the same members already qualify for.'],
];

export default function PartnersPage() {
  usePageMeta(SEO_ROUTES['/partners']);

  return (
    <div className="bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="bg-paper border-b border-v2border text-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-4">For partners</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            You bring the population.<br />
            <span className="text-v2accent">We run the money that reaches it.</span>
          </h1>
          <p className="text-xl text-ink-500 max-w-3xl mb-8">
            The Plaid for the healthcare safety net: every funder&rsquo;s interface &mdash; Medicaid plans, counties, grants
            &mdash; behind one API and an agent embedded in your product. Your members never leave the app they already trust.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to={contactHref('build', 'partner')}
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
            >
              Talk about embedding
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/developers"
              className="inline-flex items-center px-8 py-4 border-2 border-v2border text-ink text-lg font-semibold rounded-lg hover:bg-ink hover:text-paper transition-colors"
            >
              Read the API
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-800 dark:text-gray-100">
            A national family platform from the founder of a leading care marketplace{' '}
            <span className="font-semibold text-v2accent">chose to embed our ledger rather than build one.</span>
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-10 text-center">What you embed</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {WHAT_YOU_EMBED.map((w) => (
              <div key={w.title} className="rounded-xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6">
                <w.icon className="w-6 h-6 text-v2accent mb-3" aria-hidden="true" />
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{w.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{w.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Bot className="w-4 h-4 text-v2accent" aria-hidden="true" />
            The embedded agent drafts; your team or ours approves. Nothing reaches a funder unreviewed.
          </p>
        </div>
      </section>

      <OneEncounter />

      <section className="py-16 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">One lane at a time</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-3xl">
            We embed once per lane &mdash; maternal health, disability and return-to-work, Medicaid care delivery &mdash; and
            every capability one lane builds, the next one reuses.
          </p>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {LANE_STEPS.map(([name, body], i) => (
              <li key={name} className="rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <p className="text-xs font-semibold tracking-widest text-gray-400 mb-1">0{i + 1}</p>
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
